package waldo.sankan.lineamunicipalkmp.web.screens

import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.await
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import waldo.sankan.lineamunicipalkmp.model.Administrador
import waldo.sankan.lineamunicipalkmp.model.Region
import waldo.sankan.lineamunicipalkmp.model.Comuna
import waldo.sankan.lineamunicipalkmp.web.services.FirebaseWebService
import kotlin.js.Promise
import waldo.sankan.lineamunicipalkmp.model.TipoIncidencia
import androidx.compose.runtime.Stable

data class LoginAdminUiState(
    val email: String = "",
    val contrasena: String = "",
    val isLoading: Boolean = false,
    val error: String? = null,
    val rolAdmin: Administrador? = null,
    val tiposServicio: List<TipoIncidencia> = listOf(
        TipoIncidencia(id = "iluminacion_default", nombre = "iluminacion", nombreDisplay = "Iluminación"),
        TipoIncidencia(id = "basura_default", nombre = "basura", nombreDisplay = "Basura"),
        TipoIncidencia(id = "bache_default", nombre = "bache", nombreDisplay = "Bache"),
        TipoIncidencia(id = "semaforo_default", nombre = "semaforo", nombreDisplay = "Semáforo"),
        TipoIncidencia(id = "auto_abandonado_default", nombre = "auto_abandonado", nombreDisplay = "Auto Abandonado")
    ),
    val regiones: List<Region> = emptyList(),
    val comunas: List<Comuna> = emptyList(),
    val comunasFiltradas: List<Comuna> = emptyList(),
    val regionSeleccionada: Region? = null,
    val comunaSeleccionadaObj: Comuna? = null,
    val comunaSeleccionada: String = "ARICA" // Mantener por compatibilidad inicial
)

@Stable
class LoginAdminViewModel {

    private val _uiState = MutableStateFlow(LoginAdminUiState())
    val uiState = _uiState.asStateFlow()

    private val viewModelScope = CoroutineScope(Dispatchers.Main + SupervisorJob())
    private val webService = FirebaseWebService()

    init {
        // Al arrancar el login, cargamos la estructura territorial
        cargarEstructuraTerritorial()
        cargarServiciosActivos()
    }

    private fun cargarEstructuraTerritorial() {
        viewModelScope.launch {
            try {
                val comunas = webService.fetchAllComunas()
                val regiones = comunas.mapNotNull { it.regionDisplayName }.distinct().map { nombre ->
                    val ref = comunas.find { it.regionDisplayName == nombre }?.regionRef ?: nombre
                    Region(id = ref, nombre = nombre)
                }.sortedBy { it.nombre }

                // --- NIVEL NACIONAL PARA ADMINISTRADORES GENERALES ---
                val regionesConNacional = listOf(Region(id = "NACIONAL", nombre = "NIVEL NACIONAL (GENERAL)")) + regiones

                _uiState.update { it.copy(
                    regiones = regionesConNacional,
                    comunas = comunas
                ) }
                
                console.log("Login: Estructura territorial cargada. ${regionesConNacional.size} regiones.")
            } catch (e: Exception) {
                console.error("Error cargando territorio: ${e.message}")
            }
        }
    }

    fun onRegionChanged(regionId: String) {
        val region = _uiState.value.regiones.find { it.id == regionId }
        
        if (regionId == "NACIONAL") {
            // Si es Nacional, limpiamos comunas y seteamos una por defecto técnica
            _uiState.update { it.copy(
                regionSeleccionada = region,
                comunasFiltradas = emptyList(),
                comunaSeleccionadaObj = Comuna(id = "NACIONAL", comunaDisplayName = "TODO EL PAÍS", regionRef = "NACIONAL"),
                comunaSeleccionada = "NACIONAL"
            ) }
        } else {
            val filtradas = _uiState.value.comunas.filter { it.regionRef == regionId }.sortedBy { it.comunaDisplayName }
            _uiState.update { it.copy(
                regionSeleccionada = region,
                comunasFiltradas = filtradas,
                comunaSeleccionadaObj = null
            ) }
        }
    }

    fun onComunaChanged(comunaId: String) {
        val comuna = _uiState.value.comunas.find { it.id == comunaId }
        _uiState.update { it.copy(
            comunaSeleccionadaObj = comuna,
            comunaSeleccionada = comuna?.id ?: "ARICA"
        ) }
        
        // 🛡️ INTELIGENCIA DE BOTONES: Cargamos los servicios específicos de la comuna elegida
        cargarServiciosActivos()
        
        console.log("Mando Central: Jurisdicción cambiada a ${comuna?.comunaDisplayName}. Refrescando botones...")
    }

    private fun cargarServiciosActivos() {
        viewModelScope.launch {
            _uiState.update { it.copy(tiposServicio = emptyList()) }
            
            try {
                val comunaRef = _uiState.value.comunaSeleccionadaObj?.let { "comunas/${it.id}" }
                val allServices = webService.fetchAllTiposIncidencia(comunaRef = comunaRef, includeInactive = false)
                
                val serviciosAUsar = if (allServices.isNotEmpty()) {
                    allServices
                } else {
                    webService.fetchAllTiposIncidencia(null, includeInactive = false)
                }

                _uiState.update { it.copy(tiposServicio = serviciosAUsar.sortedBy { it.orden }) }
                console.log("Login: ${serviciosAUsar.size} botones de servicio listos.")
            } catch (e: Exception) {
                console.error("Error en carga de botones: ${e.message}")
            }
        }
    }

    fun onEmailChanged(email: String) {
        _uiState.update { it.copy(email = email) }
    }

    fun onContrasenaChanged(contrasena: String) {
        _uiState.update { it.copy(contrasena = contrasena) }
    }

    fun logout() {
        viewModelScope.launch {
            try {
                webService.signOut()
            } finally {
                _uiState.value = LoginAdminUiState()
                cargarEstructuraTerritorial()
                cargarServiciosActivos()
                console.log("Mando Central: Login actualizado tras logout.")
            }
        }
    }

    fun intentarLogin(rolEsperado: String) {
        val email = uiState.value.email.trim()
        val contrasena = uiState.value.contrasena.trim() 

        if (email.isBlank() || contrasena.isBlank()) {
            _uiState.update { it.copy(error = "Email y contraseña son requeridos.") }
            return
        }

        // --- VALIDACIÓN DE TERRITORIO SEGÚN ROL ---
        if (rolEsperado != "GENERAL") {
            if (uiState.value.regionSeleccionada == null || uiState.value.comunaSeleccionadaObj == null) {
                _uiState.update { it.copy(error = "Seleccione Región y Comuna antes de ingresar.") }
                return
            }
        } else {
            // Si entra como General, nos aseguramos que la jurisdicción sea Nacional
            if (uiState.value.regionSeleccionada?.id != "NACIONAL") {
                _uiState.update { it.copy(error = "Para acceso General debe seleccionar REGIÓN: NIVEL NACIONAL.") }
                return
            }
        }
        
        _uiState.update { it.copy(isLoading = true, error = null) }

        val job = viewModelScope.launch {
            val uid = webService.signInAdmin(email, contrasena)
            val adminProfile = webService.getAdminProfile(uid)

            if (adminProfile != null) {
                // 1. Limpiamos y normalizamos
                val rolDB = (adminProfile.rol ?: "").uppercase().trim()
                val rolBoton = rolEsperado.uppercase().trim()
                
                // 2. Definimos la Alianza Táctica de Roles (Mapeo de Poder)
                fun sonIguales(r1: String, r2: String): Boolean {
                    if (r1 == r2) return true
                    
                    // Alianza SEGURIDAD (Total)
                    val esSeg1 = r1.contains("SEGURIDAD") || r1 == "SEGURIDAD_ADMIN"
                    val esSeg2 = r2.contains("SEGURIDAD") || r2 == "SEGURIDAD_ADMIN"
                    if (esSeg1 && esSeg2) return true
                    
                    // Alianza SOCIAL
                    val esSoc1 = r1.contains("SOCIAL")
                    val esSoc2 = r2.contains("SOCIAL")
                    if (esSoc1 && esSoc2) return true

                    // Alianza OPERADORA
                    val esOp1 = r1 == "OPERADORA" || r1 == "OPERADOR_CENTRAL" || r1 == "CENTRAL"
                    val esOp2 = r2 == "OPERADORA" || r2 == "OPERADOR_CENTRAL" || r2 == "CENTRAL"
                    if (esOp1 && esOp2) return true

                    // Alianza ASEO
                    val esAseo1 = r1 == "ASEO" || r1 == "BASURA"
                    val esAseo2 = r2 == "ASEO" || r2 == "BASURA"
                    return esAseo1 && esAseo2
                }

                // 3. Inteligencia de Direcciones (Incluyendo el Pack DAO y Nuevos Ministerios)
                val packDAO = listOf("ASEO", "BASURA", "BACHE", "ILUMINACION", "SEMAFORO", "AUTO_ABANDONADO", "SALVAVIDAS", "SOCIAL_ADMIN", "SEGURIDAD_ADULTO_MAYOR")

                val esValido = when {
                    rolDB == "GENERAL" -> true 
                    rolDB == "COMUNAL" -> true // El Alcalde tiene permiso para entrar al panel principal
                    rolDB == "DIRECCION_DAO" && packDAO.contains(rolBoton) -> true
                    sonIguales(rolDB, rolBoton) -> true 
                    else -> false
                }

                if (esValido) {
                    _uiState.update { it.copy(isLoading = false, rolAdmin = adminProfile) }
                } else {
                    throw Exception("Acceso Denegado: Su rol es '$rolDB' pero intenta entrar como '$rolBoton'")
                }
            } else {
                throw Exception("Perfil de administrador no encontrado.")
            }
        }

        job.invokeOnCompletion { throwable ->
            if (throwable != null) {
                val mensajeError = when {
                    throwable.message?.contains("auth/invalid-credential") == true -> "Credenciales incorrectas."
                    throwable.message?.contains("INVALID_LOGIN_CREDENTIALS") == true -> "Credenciales incorrectas."
                    else -> throwable.message ?: "Error al intentar ingresar."
                }
                _uiState.update { it.copy(isLoading = false, error = mensajeError) }
            }
        }
    }
}
